import countryListModule from 'country-list';
import React, { useEffect, useState } from 'react';
import ReactSelect, { StylesConfig } from 'react-select';
import styled from 'styled-components';
import { welcomePopupService } from './welcomePopup.service';

// Define types for country-list
interface CountryList {
    getNames: () => string[];
    getCode: (name: string) => string;
}

// Import with type assertion
const countryList = countryListModule as CountryList;
const getNames = countryList.getNames;
const getCode = countryList.getCode;

interface CountryOption {
    value: string;
    label: string;
}

const countryOptions: CountryOption[] = getNames().map((country: string) => ({
    value: getCode(country),
    label: country,
}));

const customStyles: StylesConfig<CountryOption, false> = {
    control: (
        provided: Record<string, unknown>,
        state: { isFocused: boolean }
    ) => ({
        ...(provided as Record<string, unknown>),
        background: '#ffffff !important',
        backgroundColor: '#ffffff !important',
        border: state.isFocused
            ? '1.4px solid #57db9b'
            : '1px solid rgb(243, 245, 246)',
        borderRadius: '10px !important',
        minHeight: '40px',
        height: '40px',
        minWidth: '100%',
        width: '100%',
        maxWidth: '100%',
        transition: 'all 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        boxShadow: state.isFocused
            ? '0 0 0 3px rgba(186, 227, 37, 0.1)'
            : 'none',
        '&:hover': {
            borderColor: '#cbd5e1',
            background: '#ffffff !important',
            backgroundColor: '#ffffff !important',
        },
    }),
    option: (
        provided: Record<string, unknown>,
        state: { isSelected: boolean; isFocused: boolean }
    ) => ({
        ...(provided as Record<string, unknown>),
        color: '#1a1e23',
        padding: '12px 15px',
        fontSize: '14px',
        fontWeight: '400',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        borderBottom: '1px solid #f1f5f9',
        backgroundColor: state.isSelected
            ? '#e6fff2'
            : state.isFocused
            ? '#e6fff2'
            : 'transparent',
        '&:hover': {
            background: '#e6fff2',
            color: '#1a2332',
            transform: 'translateY(-1px)',
        },
        '&:last-child': {
            borderBottom: 'none',
        },
    }),
    placeholder: (provided: Record<string, unknown>) => ({
        ...(provided as Record<string, unknown>),
        color: '#6a727c',
        opacity: '0.5',
        fontWeight: '400',
        fontSize: '14px',
        paddingLeft: '0',
        margin: '0',
    }),
    singleValue: (provided: Record<string, unknown>) => ({
        ...(provided as Record<string, unknown>),
        color: '#1a1e23',
        fontWeight: '500',
        fontSize: '14px',
        paddingLeft: '0',
        margin: '0',
    }),
    menu: (provided: Record<string, unknown>) => ({
        ...(provided as Record<string, unknown>),
        background: '#fff',
        border: '1px solid #e2e5e9',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        marginTop: '4px',
        overflow: 'hidden',
        minWidth: '100%',
        width: '100%',
        maxWidth: '100%',
        maxHeight: '110px',
    }),
    valueContainer: (provided: Record<string, unknown>) => ({
        ...(provided as Record<string, unknown>),
        color: '#1a1e23',
        padding: '0 12px',
        minHeight: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    dropdownIndicator: (provided: Record<string, unknown>) => ({
        ...(provided as Record<string, unknown>),
        color: '#6a727c',
        opacity: '0.5',
        transition: 'all 0.2s ease',
        paddingRight: '8px',
        '&:hover': {
            color: '#57db9b',
        },
    }),
};

interface WelcomePopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit?: () => void;
}

interface FormData {
    name: string;
    email: string;
    country: string;
}

const WelcomePopup: React.FC<WelcomePopupProps> = ({
    isOpen,
    onClose,
    onSubmit,
}) => {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        country: '',
    });

    const [errors, setErrors] = useState<{
        name: string;
        email: string;
        country: string;
    }>({
        name: '',
        email: '',
        country: '',
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setShowPopup(true);
        }
    }, [isOpen]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear error when user starts typing
        if (errors[name as keyof typeof errors]) {
            setErrors((prev) => ({
                ...prev,
                [name]: '',
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors = {
            name: '',
            email: '',
            country: '',
        };

        // Validate name
        if (!formData.name.trim()) {
            newErrors.name = 'Full name is required';
        } else if (formData.name.trim().length < 2) {
            newErrors.name = 'Full name must be at least 2 characters';
        }

        // Validate email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = 'Email address is required';
        } else if (!emailRegex.test(formData.email.trim())) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Validate country
        if (!formData.country.trim()) {
            newErrors.country = 'Please select your country';
        }

        setErrors(newErrors);
        return !Object.values(newErrors).some((error) => error !== '');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form before submission
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitMessage('');

        try {
            const response = await welcomePopupService.submitWelcomeForm(
                formData
            );

            if (response.success) {
                setIsSuccess(true);
                setSubmitMessage(response.message);
                localStorage.setItem('contacted', 'true');
                localStorage.setItem('showWelcomePopup', 'true');

                // Open Calendly link in a new tab
                welcomePopupService.openCalendlyLink();

                // Call onSubmit callback if provided
                if (onSubmit) {
                    onSubmit();
                }

                // Close popup after showing success
                setTimeout(() => {
                    onClose();
                }, 500);
            } else {
                setSubmitMessage(response.message);
            }
        } catch (error) {
            setSubmitMessage('Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setShowPopup(false);
        setTimeout(() => {
            onClose();
        }, 300);
    };

    if (!isOpen) return null;

    return (
        <Overlay show={showPopup}>
            <PopupContainer show={showPopup}>
                <button
                    onClick={handleClose}
                    style={{
                        position: 'absolute',
                        right: '20px',
                        top: '20px',
                        border: 'none',
                        fontSize: '24px',
                        fontFamily: "'Inter', sans-serif",
                        cursor: 'pointer',
                        color: '#000',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        zIndex: 1000,
                        padding: '0',
                        margin: '0',
                    }}
                >
                    ✕
                </button>

                <Content>
                    <Header>
                        {/* <Logo>
              <LogoIcon></LogoIcon>
            </Logo> */}
                        <Title>Welcome to BlocEstate</Title>
                        <Subtitle>
                            Connect with us and book your demo today!
                        </Subtitle>
                    </Header>

                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label htmlFor='name'>
                                Full Name <RequiredAsterisk>*</RequiredAsterisk>
                            </Label>
                            <Input
                                type='text'
                                id='name'
                                name='name'
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder='Enter your full name'
                                className={errors.name ? 'error' : ''}
                                required
                            />
                            {errors.name && (
                                <ErrorMessage>{errors.name}</ErrorMessage>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor='email'>
                                Email Address{' '}
                                <RequiredAsterisk>*</RequiredAsterisk>
                            </Label>
                            <Input
                                type='email'
                                id='email'
                                name='email'
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder='Enter your email address'
                                className={errors.email ? 'error' : ''}
                                required
                            />
                            {errors.email && (
                                <ErrorMessage>{errors.email}</ErrorMessage>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor='country'>
                                Country <RequiredAsterisk>*</RequiredAsterisk>
                            </Label>
                            <ReactSelect
                                options={countryOptions}
                                styles={customStyles}
                                placeholder='Select your country'
                                value={countryOptions.find(
                                    (option: CountryOption) =>
                                        option.value === formData.country
                                )}
                                onChange={(option: CountryOption | null) => {
                                    if (option) {
                                        setFormData((prev) => ({
                                            ...prev,
                                            country: option.value,
                                        }));
                                        // Clear error when country is selected
                                        if (errors.country) {
                                            setErrors((prev) => ({
                                                ...prev,
                                                country: '',
                                            }));
                                        }
                                    }
                                }}
                                isSearchable
                                isClearable={false}
                                className={errors.country ? 'error' : ''}
                            />
                            {errors.country && (
                                <ErrorMessage>{errors.country}</ErrorMessage>
                            )}
                        </FormGroup>

                        {/* {submitMessage && (
              <MessageBox isSuccess={isSuccess}>
                {submitMessage}
              </MessageBox>
            )} */}

                        <ButtonGroup>
                            <SubmitButton type='submit' disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <>
                                        <Spinner />
                                        Submitting...
                                    </>
                                ) : isSuccess ? (
                                    'Close'
                                ) : (
                                    'Connect'
                                )}
                            </SubmitButton>
                        </ButtonGroup>
                    </Form>
                </Content>
            </PopupContainer>
        </Overlay>
    );
};

// Styled Components
const Overlay = styled.div<{ show: boolean }>`
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.85);
    z-index: 10;
    backdrop-filter: blur(5px);
    //-webkit-backdrop-filter: blur(25px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    transform: translateZ(0);
    min-height: 100vh;
    height: 100vh;
    will-change: backdrop-filter;
    opacity: ${(props) => (props.show ? 1 : 0)};
    transition: opacity 0.3s ease-in-out;

    @media (max-width: 768px) {
        padding: 15px;
    }

    @media (max-width: 480px) {
        padding: 10px;
    }

    @media (max-width: 360px) {
        padding: 8px;
    }
`;

const PopupContainer = styled.div<{ show: boolean }>`
    width: 100%;
    position: relative;
    max-width: 450px;
    margin: 0 auto;
    max-height: 90vh;
    min-height: 450px;
    background: #ffffff;
    border-radius: 12px;
    justify-content: flex-start;
    align-items: stretch;
    color: #1a1e23;
    flex-direction: column;
    transform: ${(props) => (props.show ? 'scale(1)' : 'scale(0.9)')};
    transition: transform 0.3s ease-in-out;
    overflow: visible;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    display: flex;

    @media (max-width: 768px) {
        max-width: 400px;
        margin: 0 20px;
        min-height: 380px;
        max-height: 85vh;
        border-radius: 12px;
        overflow: visible;
    }

    @media (max-width: 480px) {
        max-width: 100%;
        margin: 0 15px;
        min-height: 350px;
        max-height: 90vh;
        border-radius: 10px;
        overflow: visible;
    }

    @media (max-width: 360px) {
        margin: 0 10px;
        min-height: 320px;
        max-height: 95vh;
        border-radius: 8px;
    }
`;

const Content = styled.div`
    padding: 30px 25px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    text-align: left;
    width: 100%;
    overflow: visible;

    @media (max-width: 768px) {
        padding: 25px 20px;
        overflow: visible;
    }

    @media (max-width: 480px) {
        padding: 20px 15px;
        overflow: visible;
    }

    @media (max-width: 360px) {
        padding: 15px 10px;
        overflow: visible;
    }
`;

const Header = styled.div`
    text-align: left;
    margin-bottom: 25px;
    width: 100%;

    @media (max-width: 480px) {
        margin-bottom: 20px;
    }

    @media (max-width: 360px) {
        margin-bottom: 15px;
    }
`;

const Title = styled.h2`
    color: #1a1e23;
    font-size: 24px;
    font-weight: 700;
    font-family: 'Inter', sans-serif;
    margin-bottom: 12px;
    letter-spacing: -0.5px;
    line-height: 1.2;

    @media (max-width: 768px) {
        font-size: 22px;
        margin-bottom: 10px;
    }

    @media (max-width: 480px) {
        font-size: 20px;
        margin-bottom: 8px;
    }

    @media (max-width: 360px) {
        font-size: 18px;
        margin-bottom: 6px;
    }
`;

const Subtitle = styled.p`
    color: #6a727c;
    font-size: 14px;
    line-height: 1.5;
    margin: 0;
    font-weight: 400;
    font-family: 'Inter', sans-serif;
    max-width: 320px;
    opacity: 0.8;

    @media (max-width: 768px) {
        font-size: 13px;
        max-width: 280px;
    }

    @media (max-width: 480px) {
        font-size: 12px;
        max-width: 260px;
    }

    @media (max-width: 360px) {
        font-size: 11px;
        max-width: 240px;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 100%;
    align-items: flex-start;

    @media (max-width: 768px) {
        gap: 14px;
    }

    @media (max-width: 480px) {
        gap: 12px;
    }

    @media (max-width: 360px) {
        gap: 10px;
    }

    /* Force white background on all inputs */
    input {
        background: #ffffff !important;
        background-color: #ffffff !important;
        -webkit-appearance: none !important;
        -moz-appearance: none !important;
        appearance: none !important;
    }

    input:focus {
        background: #ffffff !important;
        background-color: #ffffff !important;
    }

    input:hover {
        background: #ffffff !important;
        background-color: #ffffff !important;
    }
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
    width: 100%;

    @media (max-width: 480px) {
        gap: 5px;
        margin-bottom: 10px;
    }

    @media (max-width: 360px) {
        gap: 4px;
        margin-bottom: 8px;
    }
`;

const Label = styled.label`
    color: #1a1e23;
    font-size: 14px;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    opacity: 0.8;
    letter-spacing: -0.2px;
    margin-bottom: 8px;

    @media (max-width: 480px) {
        font-size: 13px;
        margin-bottom: 6px;
    }

    @media (max-width: 360px) {
        font-size: 12px;
        margin-bottom: 5px;
    }
`;

const ErrorMessage = styled.span`
    color: #ff4d4f;
    font-size: 12px;
    font-weight: 400;
    font-family: 'Inter', sans-serif;
    margin-top: 4px;
    display: block;
`;

const RequiredAsterisk = styled.span`
    color: #ff4d4f;
    font-weight: 700;
`;

const Input = styled.input`
    width: 100%;
    background: #ffffff !important;
    background-color: #ffffff !important;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    box-shadow: none !important;
    -webkit-box-shadow: none !important;
    -moz-box-shadow: none !important;
    border: 1px solid rgb(243, 245, 246);
    border-radius: 10px !important;
    padding: 10px 14px;
    color: #1a1e23;
    font-size: 14px;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    transition: all 0.3s ease;

    @media (max-width: 768px) {
        padding: 9px 12px;
        font-size: 13px;
    }

    @media (max-width: 480px) {
        padding: 8px 10px;
        font-size: 12px;
        border-radius: 8px !important;
    }

    @media (max-width: 360px) {
        padding: 7px 8px;
        font-size: 11px;
        border-radius: 6px !important;
    }

    &::placeholder {
        color: #6a727c;
        opacity: 0.5;
        font-weight: 400;
        background: #ffffff !important;
        background-color: #ffffff !important;
    }

    &:focus {
        outline: none;
        border: 1.4px solid #57db9b;
        border-color: #57db9b;
        background: #ffffff !important;
        background-color: #ffffff !important;
    }

    &:hover {
        background: #ffffff !important;
        background-color: #ffffff !important;
    }

    &.error {
        border-color: #ff4d4f;
        box-shadow: 0 0 0 3px rgba(255, 77, 79, 0.1);
        background: #ffffff !important;
        background-color: #ffffff !important;
    }

    /* Override any browser defaults */
    &::-webkit-input-placeholder {
        background: #ffffff !important;
        background-color: #ffffff !important;
    }

    &::-moz-placeholder {
        background: #ffffff !important;
        background-color: #ffffff !important;
    }

    &:-ms-input-placeholder {
        background: #ffffff !important;
        background-color: #ffffff !important;
    }

    /* Override autofill styles */
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px #ffffff inset !important;
        -webkit-text-fill-color: #1a1e23 !important;
        background: #ffffff !important;
        background-color: #ffffff !important;
        transition: background-color 5000s ease-in-out 0s;
    }

    /* Additional autofill overrides */
    &:-webkit-autofill {
        background-clip: content-box !important;
        background: #ffffff !important;
        background-color: #ffffff !important;
    }
`;

const ButtonGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
    width: 100%;
`;

const SubmitButton = styled.button`
    width: 100%;
    font-family: 'Inter', sans-serif !important;
    font-size: 16px;
    font-weight: 600;
    border-radius: 10px !important;
    min-height: 44px;
    padding: 10px 20px;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    background: #28bd75;
    color: #ffffff;
    box-shadow: 0 4px 12px rgba(40, 189, 117, 0.3);
    text-transform: none;
    letter-spacing: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:hover:not(:disabled) {
        background: #1e9f63;
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(40, 189, 117, 0.4);
    }

    &:active {
        transform: translateY(0);
    }

    &:disabled {
        background: #9ca3af;
        color: #ffffff;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
        opacity: 0.7;
    }

    @media (max-width: 768px) {
        min-height: 42px;
        font-size: 15px;
        padding: 9px 18px;
    }

    @media (max-width: 480px) {
        min-height: 40px;
        font-size: 14px;
        padding: 8px 16px;
        border-radius: 8px !important;
    }

    @media (max-width: 360px) {
        min-height: 38px;
        font-size: 13px;
        padding: 7px 14px;
        border-radius: 6px !important;
    }
`;

const Spinner = styled.div`
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

export default WelcomePopup;
